// ./src/utils/planEntitlement.ts
//
// "Does this account's plan allow this?" — the CLIENT's answer, and it must be the same
// answer the server gives.
//
// ⭐ This is a deliberate MIRROR of eventsmagic-api `src/middleware/planFeatureGate.ts`.
// Read that file before changing anything here. Every rule below exists there, in the same
// order, for the same reason; the two disagreeing is worse than either being wrong, because
// the UI would then either lock a control the server allows (a customer blocked by nothing)
// or offer one the server refuses (an unexplained failure at save time, which is the exact
// state A2.5.1 exists to end).
//
// It is a PURE function of the plan rows — no React, no context, no network — so it can be
// exercised directly and so the seam in `useFeatureGate` has one thing to call.
//
// ⚠ What this deliberately does NOT do: resolve an EVENT-scoped grant. A feature can be
// unlocked by the account's subscription *or* by that one event's upgrade (MONETIZATION.md
// §2), and neither the server gate nor ms_auth answers the event half yet. That resolution
// belongs in `useFeatureGate`, which is the single place allowed to answer the question.
import { OpResult } from "@sdflc/api-helpers";
import { getFeatureValue } from "@em/utils";

import { FEATURE_DISPLAY, FEATURE_NAMES, FEATURE_VALUE_TYPES } from "@/config/featureCodes.generated";
import { AppPlanFeatureDetails } from "@/utils/api/AppPlanService";

/** Why the gate answered the way it did. Drives the copy, never the allow/deny itself. */
export type EntitlementReason =
  /** The plan includes it. */
  | "included"
  /** No plan could be read, or the plan carries no rows — fail OPEN (server rule 2). */
  | "no_plan"
  /** The plan has rows but not this one: a catalog gap on our side — fail OPEN. */
  | "not_provisioned"
  /** A numeric ceiling asked without a count — nothing to compare, so fail OPEN. */
  | "no_count"
  /** A numeric ceiling whose declared `zeroMeans` is "unlimited", or an unreadable value. */
  | "unlimited"
  /** An on/off capability the plan does not include. */
  | "capability"
  /** A numeric ceiling that is already full. */
  | "limit"
  /** ROADMAP #231 d — the event is in TEST MODE: everything may be tried; going live asks the plan (server rule 6). */
  | "test_mode"
  /** ROADMAP #231 — the event is a DRAFT: everything may be tried; publishing asks the plan (server rule 6). */
  | "draft"
  /** ROADMAP #209 — the plan bought for this event includes it (partylabz sells one event at a time). */
  | "event_upgrade";

export interface EntitlementVerdict {
  /**
   * The feature this verdict is about.
   *
   * ⚠ Carried so the verdict is SELF-DESCRIBING. `PlanFeatureNotice` receives a verdict and
   * nothing else, so without this it cannot tell which feature it is rendering for — and it
   * has to know, to check itself against that feature's declared gate kind (`planGating.ts`).
   * The alternative was a second prop on all 23 call sites, which is one more thing to pass
   * wrongly for a fact the verdict already implies.
   */
  code: string;
  allowed: boolean;
  reason: EntitlementReason;
  /** The plan's ceiling for a numeric feature; null for a capability or when unreadable. */
  limit: number | null;
  /** The customer-facing feature name, for the locked control's copy. */
  featureName: string;
  /**
   * The sentence to show when `allowed` is false — WORD FOR WORD what the server would have
   * returned, so a host cannot be told two different things about one refusal.
   */
  message: string | null;
  /**
   * ROADMAP #231 — neither the plan nor the event's own upgrade includes it: refused, OR allowed only because the event
   * is a draft / in test mode (reason `draft` / `test_mode`) — publishing or going live will ask for it. What a gate's
   * premium marker reads. Absent = false.
   */
  premium?: boolean;
  /**
   * #238 — a BACK-OFFICE tool on a draft or a test run: its sample (`utils/planTrial.ts`). Set when the page asked with a
   * sample — allowed while `count` is under `limit` (or not known — the server decides), refused past it. What the marker
   * and the notice say. Absent otherwise.
   */
  sample?: { count: number | null; limit: number; noun: readonly [string, string]; publishAsks: boolean };
}

/** The plan rows, keyed by feature code — the shape AppContext already holds. */
export type PlanFeatureMap = Record<string, AppPlanFeatureDetails> | null | undefined;

/**
 * Whether a feature is an on/off capability rather than a numeric ceiling.
 *
 * ⚠ Reads the DECLARED value type (generated from the API's `appFeatures.ts`), exactly as
 * the server's `isBooleanFeature` does — not `FEATURE_DISPLAY[code].format`, which is a
 * rendering hint and can legitimately differ.
 */
export const isBooleanFeature = (code: string): boolean => {
  const type = String(FEATURE_VALUE_TYPES[code] || "").toLowerCase().trim();

  return type === "boolean" || type === "bool";
};

/** What a literal `0` means for this feature. Declared per feature, NEVER inferred. */
export const featureZeroMeans = (code: string): "unlimited" | "none" | undefined => {
  return FEATURE_DISPLAY[code]?.zeroMeans;
};

/**
 * The customer-facing name.
 *
 * ⚠ The GENERATED name wins, not the plan row's. The server's `planFeatureGate` builds its
 * refusal from `featureDisplayName(code)` — the static declaration in `appFeatures.ts`, which
 * is exactly what this file's `FEATURE_NAMES` is generated from. Preferring `detail.featureName`
 * (the ms_auth row) would be right only for as long as the two never drift, and a row
 * provisioned before a rename would have the host reading two different names for one refusal
 * depending on whether the client or the server produced it. The row is the fallback.
 */
export const featureDisplayName = (code: string, detail?: AppPlanFeatureDetails | null): string => {
  return FEATURE_NAMES[code] || detail?.featureName || code;
};

const allow = (
  code: string,
  reason: EntitlementReason,
  featureName: string,
  limit: number | null = null
): EntitlementVerdict => {
  return { code, allowed: true, reason, limit, featureName, message: null };
};

/**
 * The whole rule set, in the server's order.
 *
 * @param features     the account's plan rows, keyed by feature code
 * @param code         the feature to check
 * @param currentCount for a NUMERIC ceiling: how many already exist. `currentCount >= limit`
 *                     refuses, so pass the count BEFORE adding — same contract as the server.
 */
export const evaluateEntitlement = (
  features: PlanFeatureMap,
  code: string,
  currentCount?: number | null
): EntitlementVerdict => {
  const detail = features?.[code];
  const featureName = featureDisplayName(code, detail);

  // Rule 2 — fail OPEN when there is no plan to read. A signed-in user whose account row has
  // not loaded yet lands here on the first render, and a lock that flickers on every reload
  // is indistinguishable from a real refusal.
  if (!features || Object.keys(features).length === 0) {
    return allow(code, "no_plan", featureName);
  }

  // An unprovisioned feature is a catalog gap on our side, not a missing entitlement.
  if (!detail) {
    return allow(code, "not_provisioned", featureName);
  }

  const parsed = getFeatureValue(detail.featureValue, detail.featureValueType);

  // ── On/off capability ──────────────────────────────────────────────────────────────
  if (isBooleanFeature(code)) {
    if (parsed === true) {
      return allow(code, "included", featureName);
    }

    return {
      code,
      allowed: false,
      reason: "capability",
      limit: null,
      featureName,
      message: `${featureName} is not included in your current plan.`,
    };
  }

  // ── Numeric ceiling ────────────────────────────────────────────────────────────────
  if (currentCount == null) {
    // Nothing to compare against. The server treats this as a caller bug and allows; so do
    // we, rather than inventing a verdict the server would not have reached.
    return allow(code, "no_count", featureName, null);
  }

  const limit = Number(parsed);

  if (!Number.isFinite(limit)) {
    return allow(code, "unlimited", featureName, null);
  }

  // ⚠ `0` is a sentinel whose meaning is DECLARED per feature. 0 reminders means unlimited;
  // 0 messages means none. Reading it either way by habit gets one of them backwards.
  if (limit === 0) {
    const zeroMeans = featureZeroMeans(code);

    if (zeroMeans === "none") {
      return {
        code,
        allowed: false,
        reason: "capability",
        limit: 0,
        featureName,
        message: `${featureName} is not included in your current plan.`,
      };
    }

    // "unlimited", or undeclared — we do not guess.
    return allow(code, "unlimited", featureName, null);
  }

  if (currentCount >= limit) {
    return {
      code,
      allowed: false,
      reason: "limit",
      limit,
      featureName,
      message: `${featureName}: your plan allows ${limit}. Upgrade to add more.`,
    };
  }

  return allow(code, "included", featureName, limit);
};

/** Plan feature rows (as the API returns them) → the map the gate reads. Rows without a code are dropped. */
export const planFeatureMapOf = (
  rows: ReadonlyArray<{ featureCode?: string | null; featureName?: string | null; featureValue?: string | null; featureValueType?: string | null; status?: number | null }>,
): Record<string, AppPlanFeatureDetails> =>
  Object.fromEntries(
    rows
      .filter((f) => !!f?.featureCode)
      .map((f) => [
        String(f.featureCode),
        {
          featureCode: String(f.featureCode),
          featureName: f.featureName ?? String(f.featureCode),
          featureValue: f.featureValue ?? "",
          featureValueType: f.featureValueType ?? "",
          status: f.status ?? 0,
        } as AppPlanFeatureDetails,
      ]),
  );

/**
 * ROADMAP #209 — does a BOUGHT plan (an event's upgrade) grant this? ⚠ Only what it STATES, as the server's judge does: a
 * feature it does not carry, a ceiling asked without a count, or a value that is not a number is NO grant — never the
 * account plan's fail-open. `currentCount` is the count before adding one (a total past the limit: pass total - 1).
 */
export const boughtPlanGrants = (features: PlanFeatureMap, code: string, currentCount?: number | null): boolean => {
  const detail = features?.[code];

  if (!detail) {
    return false;
  }

  const verdict = evaluateEntitlement(features, code, currentCount);

  return verdict.allowed && (verdict.reason === "included" || (verdict.reason === "unlimited" && Number.isFinite(Number(detail.featureValue))));
};

/**
 * The verdict as an OpResult, for a call site that wants to surface it the same way a server
 * refusal is surfaced. Keyed on `plan`, exactly as `planFeatureGate` keys its error, so one
 * error-rendering path handles both.
 */
export const entitlementToOpResult = (verdict: EntitlementVerdict): OpResult => {
  if (verdict.allowed) {
    return OpResult.ok();
  }

  return new OpResult().addError("plan", verdict.message || "Not included in your current plan.");
};

/**
 * #142 — WHICH plan decides a create: the account the record will belong to, as the server's `planFeatureGate` does.
 * In an event's workspace that is the EVENT's account — for its owner the same as their own, for a crew member from
 * another account NOT their own plan. So:
 *   • a gate scoped to the workspace's event (or any gate while working an event as crew) reads the event account's
 *     plan (`myEventAccess.planFeatures`);
 *   • the event plan unknown — the owner's own plan stands in (it IS the event account's); a crew member's gate fails
 *     open (null), as the server does when the plan cannot be read;
 *   • anything else (the account's own pages, another event) reads the caller's own plan.
 */
export const planFeaturesFor = (
  own: PlanFeatureMap,
  workspace: { eventId: string | null; ownsEvent: boolean; planFeatures: PlanFeatureMap | null },
  scopeEventId?: string | null,
): PlanFeatureMap => {
  // Every gate on an event's pages is about that event (a gate naming ANOTHER event is not) — one answer per page.
  const forWorkspace = !!workspace.eventId && (!scopeEventId || scopeEventId === workspace.eventId);

  if (!forWorkspace) {
    return own;
  }

  if (workspace.planFeatures) {
    return workspace.planFeatures;
  }

  // ⚠ The event plan not read yet (or unreadable): the person's OWN event → their own plan IS its account's; anyone
  //   else (crew, or not known yet) → fail open (null), as the server does — never a crew member's own plan, which
  //   would flash (or keep) a lock their event's plan does not have.
  return workspace.ownsEvent ? own : null;
};
